import { useAuthFetchWithResult } from '@e3dc-react/shell/Hooks/AuthFetch/AuthFetch'
import { useTypedSelector } from 'src/State/RootReducer'
import { WallboxModel } from '../models/wallbox-model'
import { WallboxDto } from './dto/wallbox.dto'
import { wallboxDtoToModel } from '../transformers/wallbox-dto-to-model.transformer'
import { MOCK_WALLBOXES } from '../../guided-tour/mock-data/mock-wallboxes'
import { wallboxSortFn } from '../../shared/utils/sort-wallboxes'
import { usePortalQuery } from 'src/Libs/api'

interface UseFetchWallboxesHook {
  wallboxes: WallboxModel[]
  isLoading: boolean
  isError: boolean
  isSuccess: boolean
}

/**
 * Function for fetching wallbox data
 * @param installationId - unique installation identifier as string
 * @returns wallboxes as array of WallboxModel, isLoading as boolean, isSuccess as boolean and isError as boolean
 */
export const useFetchWallboxes = (installationId: number | undefined, isInstallationDeleted?: boolean): UseFetchWallboxesHook => {
  const authFetch = useAuthFetchWithResult()
  const eMobilityTourRunning = useTypedSelector(state => state.eMobility.tour.isRunning)

  const fetchWallboxes = (installationId: number): Promise<WallboxDto[]> => {
    return authFetch<WallboxDto[]>({
      path: `wallbox/wallboxes/${installationId}/active`,
      endpoint: process.env.REACT_APP_HAGER_BRIDGE_SERVICE,
      method: 'GET',
    })
  }

  const { data, isLoading, isError, isSuccess } = usePortalQuery({
    queryFn: () => (eMobilityTourRunning ? Promise.resolve(MOCK_WALLBOXES) : fetchWallboxes(Number(installationId))),
    queryKey: ['wallboxes', installationId],
    refetchOnWindowFocus: false,
    initialData: eMobilityTourRunning ? MOCK_WALLBOXES : undefined,
    /**
     * Always refetch wallboxes on mount because assigned tariffs can be modified outside e-mobility section(in settings/smart functions).
     *
     * A "proper" approach would be to trigger the invalidation manually from the smart functions page after changes are saved,
     * but for now we will keep the e-mobility section independent and resilient to changes in outside code. Reduces chance of
     * incorrect queryKey invalidated due to runtime type of installation id(number vs string).
     */
    refetchOnMount: 'always',
    enabled: Boolean(installationId) && !isInstallationDeleted,
    select: response => (response ?? []).map(wallboxDtoToModel).sort(wallboxSortFn),
  })

  return { wallboxes: data || [], isLoading, isError, isSuccess }
}
